import React from 'react';
import { Button, Box, Typography, IconButton, Chip, alpha } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import AlertDialog from './AlertDialog';
import useAlertDialog from '../../hooks/useAlertDialog';

const FileUploadComponent = ({
  label,
  files = [],
  onFileUpload,
  onFileRemove,
  accept = '.pdf,.jpg,.jpeg,.png',
  multiple = false,
  required = false,
  disabled = false,
  buttonVariant = 'outlined',
  showChips = true,
  maxSize = 4 * 1024 * 1024, // 4MB default
  maxSizeError = 'File size should not exceed 4MB',
  error = false,
  helperText
}) => {
  const theme = useTheme();
  const { alertDialog, showAlert, closeAlert } = useAlertDialog();

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Validate file sizes
    const oversizedFiles = selectedFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      showAlert({
        title: 'File Size Error',
        message: maxSizeError,
        severity: 'warning'
      });
      return;
    }

    if (onFileUpload) {
      onFileUpload(selectedFiles);
    }

    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    if (onFileRemove) {
      onFileRemove(index);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const truncateFileName = (fileName, maxLength = 20) => {
    if (fileName.length <= maxLength) return fileName;
    return fileName.substring(0, maxLength - 3) + '...';
  };

  return (
    <Box>
      <Button
        variant={buttonVariant}
        component="label"
        disabled={disabled}
        size="small"
        sx={{
          borderRadius: 2,
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
          color: error ? theme.palette.error.main : theme.palette.primary.main,
          '&:hover': {
            borderColor: error ? theme.palette.error.dark : theme.palette.primary.dark,
            backgroundColor: theme.palette.action.hover,
          },
          width: '100%',
          height: '40px',
          justifyContent: 'flex-start'
        }}
      >
        <CloudUpload sx={{ mr: 1 }} />
        {label} {required && '*'}
        <input
          type="file"
          hidden
          multiple={multiple}
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled}
        />
      </Button>
      
      {(helperText || maxSize) && (
        <Typography 
          variant="caption" 
          color={error ? "error" : "textSecondary"} 
          sx={{ mt: 0.5, ml: 2, display: 'block', fontWeight: 500 }}
        >
          {helperText} {maxSize ? `(Max file size: ${typeof maxSize === 'number' ? Math.round(maxSize / (1024 * 1024)) + 'MB' : maxSize})` : ''}
        </Typography>
      )}

      {files && Array.isArray(files) && files.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {showChips ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {files.map((file, index) => (
                <Chip
                  key={index}
                  label={truncateFileName(file.name)}
                  size="small"
                  onDelete={disabled ? null : () => handleRemoveFile(index)}
                  onClick={file.url ? () => window.open(file.url, '_blank') : undefined}
                  sx={{ 
                    borderRadius: 1,
                    cursor: file.url ? 'pointer' : 'default',
                    '&:hover': file.url ? { bgcolor: alpha(theme.palette.primary.main, 0.1) } : {}
                  }}
                  title={file.url ? `Click to view ${file.name}` : `${file.name} (${formatFileSize(file.size)})`}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {files.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ mr: 0.5, fontSize: '0.7rem' }}
                    title={`${file.name} (${formatFileSize(file.size)})`}
                  >
                    {truncateFileName(file.name, 15)}
                  </Typography>
                  {!disabled && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      sx={{ color: theme.palette.error.main, p: 0.25 }}
                    >
                      ×
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      
      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
        confirmText={alertDialog.confirmText}
        cancelText={alertDialog.cancelText}
        showCancel={alertDialog.showCancel}
        onConfirm={alertDialog.onConfirm}
      />
    </Box>
  );
};

export default FileUploadComponent;
